import React, { useMemo, useEffect } from 'react';

import { Input } from './input';
import { humanizeEnum } from '../../utils';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';

interface Props<T extends object> {
  id: string;
  label?: string;
  data: T[];
  setFilteredList: React.Dispatch<React.SetStateAction<T[]>>;
  searchBy?: (keyof T)[] | keyof T;
  defaultValue?: string;
}

export function SearchInput<T extends object>({ id, label, data, setFilteredList, searchBy, defaultValue }: Props<T>) {
  const { bind: search } = useDebouncedInput(defaultValue ?? '');

  const searchByArray: (keyof T)[] = useMemo(() => {
    if (Array.isArray(searchBy)) {
      return searchBy;
    }
    if (searchBy) {
      return [searchBy];
    }
    return [];
  }, [searchBy]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const searchValue = search.value.toLowerCase();

    if (!searchValue) return data;

    return data.filter((item) => {
      if (searchByArray.length > 0) {
        return searchByArray.some((key) => {
          const value = String(item[key] || '').toLowerCase();
          return value.includes(searchValue);
        });
      }
      return Object.values(item as object)
        .join(' ')
        .toLowerCase()
        .includes(searchValue);
    });
  }, [search.value, data, searchByArray]);

  useEffect(() => {
    setFilteredList((prev) => (JSON.stringify(prev) === JSON.stringify(filteredData) ? prev : filteredData));
  }, [filteredData]);

  return (
    <>
      <Input id={id} className="hdn-input hdn-input--transparent-placeholder" placeholder="" {...search} type="search" autoFocus />
      <label htmlFor={id} className="hdn-input__label">
        {label ||
          `Search by ${searchByArray.length > 0 ? searchByArray.map((key) => humanizeEnum(String(key), false)).join(', ') : 'any field'}`}
      </label>
    </>
  );
}
